/* global server */
import {
  BILLING,
  DELIVERIES_ADMIN,
  EMPLOYEE,
  GLOBAL_ADMIN,
  LOCATION_ADMIN,
  RECEPTIONIST,
  SECURITY,
  ZONE_ADMIN,
  ZONE_SECURITY,
} from 'garaje/utils/roles';
import PermissionSets from 'garaje/mirage/helpers/permission-sets';
import { faker } from '@faker-js/faker';
import { Permission } from 'garaje/utils/ui-permissions';
import { RoleId, RoleScopeType, getLegacyRoleName } from 'garaje/utils/custom-roles';

// Map legacy role string names to `id` values
const ROLE_NAME_TO_ID = {
  [BILLING]: RoleId.BILLING_ADMIN,
  [DELIVERIES_ADMIN]: RoleId.DELIVERIES_ADMIN,
  [EMPLOYEE]: RoleId.EMPLOYEE,
  [GLOBAL_ADMIN]: RoleId.GLOBAL_ADMIN,
  [LOCATION_ADMIN]: RoleId.LOCATION_ADMIN,
  [RECEPTIONIST]: RoleId.FRONT_DESK_ADMIN,
  [SECURITY]: RoleId.SECURITY_ADMIN,
  [ZONE_ADMIN]: RoleId.ZONE_ADMIN,
  [ZONE_SECURITY]: RoleId.ZONE_SECURITY_ADMIN,
};

// For the roles listed here, override the legacy role name with this value.
const ROLE_DISPLAY_NAME = {
  [BILLING]: 'Billing Admin',
  [RECEPTIONIST]: 'Front Desk Admin',
  [ZONE_ADMIN]: 'Property Admin',
  [ZONE_SECURITY]: 'Property Security Admin',
};

const ALL_PERMISSIONS = Object.values(Permission);

// Most users have these permissions, regardless of role. (These are not customizable through the UI.)
const BASE_PERMISSIONS = [Permission.VISITORS_ENTRY_READ_MINE, Permission.VISITORS_INVITE_READ_MINE];

const COMPANY_PERMISSIONS_BY_ROLE = {
  [BILLING]: [...PermissionSets.ACCOUNT_MANAGEMENT['Manage billing details']],
  [GLOBAL_ADMIN]: ALL_PERMISSIONS,
};

const LOCATION_PERMISSIONS_BY_ROLE = {
  [BILLING]: [],
  [DELIVERIES_ADMIN]: [
    ...BASE_PERMISSIONS,
    ...PermissionSets.ANALYTICS['View Deliveries analytics'],

    ...PermissionSets.DELIVERIES['View all deliveries'],
    ...PermissionSets.DELIVERIES['Add deliveries (mobile app)'],
    ...PermissionSets.DELIVERIES['Edit and delete deliveries'],
  ],
  [EMPLOYEE]: [Permission.VISITORS_INVITE_CREATE, Permission.VISITORS_INVITE_READ_MINE],
  [GLOBAL_ADMIN]: ALL_PERMISSIONS,
  [LOCATION_ADMIN]: ALL_PERMISSIONS,
  [RECEPTIONIST]: [
    ...BASE_PERMISSIONS,
    ...PermissionSets.VISITORS['View all visitor entries'],
    ...PermissionSets.VISITORS['Add visitor entries'],
    ...PermissionSets.VISITORS['Edit visitor entries'],
    ...PermissionSets.VISITORS['Export all visitor entries'],
    ...PermissionSets.VISITORS['View all invites'],
    ...PermissionSets.VISITORS['Edit all invites'],
    ...PermissionSets.VISITORS['Delete all invites'],
    ...PermissionSets.VISITORS['Export all invites'],
    ...PermissionSets.VISITORS['View devices'],

    ...PermissionSets.ANALYTICS['View Visitors analytics'],
    ...PermissionSets.ANALYTICS['View Deliveries analytics'],
    ...PermissionSets.ANALYTICS['View Desks analytics'],

    ...PermissionSets.WORKPLACE['Manage employee log'],
    ...PermissionSets.WORKPLACE['Export employee log'],

    ...PermissionSets.MAPS['Manage live maps & neighborhoods'],

    ...PermissionSets.DESKS['Manage desks & neighborhoods'],
    ...PermissionSets.DESKS['Manage desk settings'],
    ...PermissionSets.DESKS['Manage desk reservations'],

    ...PermissionSets.USER_MANAGEMENT['View admins'],
  ],
  [SECURITY]: [
    Permission.VISITORS_SECURITY_DESK_READ,
    ...BASE_PERMISSIONS,
    ...PermissionSets.VISITORS['View all invites'],
    ...PermissionSets.VISITORS['Edit all invites'],
    ...PermissionSets.VISITORS['Delete all invites'],
  ],
};

const ZONE_PERMISSIONS_BY_ROLE = {
  [ZONE_ADMIN]: [
    /* not yet implemented */
  ],
  [ZONE_SECURITY]: [
    /* not yet implemented */
  ],
};

function getRoleName(roleId) {
  return ROLE_DISPLAY_NAME[roleId] ?? getLegacyRoleName(roleId);
}

export function assignCompanyRoleToUser({
  company,
  roleId,
  user,
  editable = true,
  employee = null,
  lastSeenAt,
  manuallyAdded = true,
}) {
  if (!roleId) {
    throw 'You called assignCompanyRoleToUser but did not specify a value for `roleId`.';
  }
  const role = server.schema['authz/roles'].find(roleId);
  if (!role) {
    throw `You called assignCompanyRoleToUser with roleId: "${roleId}", but this role doesn't exist.`;
  }
  const legacyRole = server.create('company-role', {
    user,
    company,
    roleName: role.legacyRole,
    employee,
    lastSeenAt,
  });

  const roleAssignment = server.create('role-assignment', {
    editable,
    manuallyAdded,
    userId: user.id,
    roleId,
    roleScopeId: company?.id,
    roleScopeName: company?.name,
    roleScopeType: RoleScopeType.COMPANY,

    _lastSeenAt: lastSeenAt,
  });

  return { role: legacyRole, roleAssignment };
}

export function assignLocationRoleToUser({
  location,
  roleId,
  user,
  editable = true,
  employee = null,
  lastSeenAt,
  manuallyAdded = true,
}) {
  if (!roleId) {
    throw 'You called assignLocationRoleToUser but did not specify a value for `roleId`.';
  }
  const role = server.schema['authz/roles'].find(roleId);
  if (!role) {
    throw `You called assignLocationRoleToUser with roleId: "${roleId}", but this role doesn't exist.`;
  }
  const legacyRole = server.create('location-role', {
    location,
    roleName: role.legacyRole,
    user,
    employee,
    lastSeenAt,
  });

  const roleAssignment = server.create('role-assignment', {
    editable,
    manuallyAdded,
    userId: user.id,
    roleId,
    roleScopeId: location?.id,
    roleScopeName: location?.name,
    roleScopeType: RoleScopeType.LOCATION,

    _lastSeenAt: lastSeenAt,
  });

  return { role: legacyRole, roleAssignment };
}

export function assignZoneRoleToUser({
  roleId,
  user,
  zone,
  editable = true,
  employee = null,
  confirmed = true,
  lastSeenAt,
  manuallyAdded = true,
}) {
  if (!roleId) {
    throw 'You called assignZoneRoleToUser but did not specify a value for `roleId`.';
  }
  const role = server.schema['authz/roles'].find(roleId);
  if (!role) {
    throw `You called assignZoneRoleToUser with roleId: "${roleId}", but this role doesn't exist.`;
  }
  const legacyRole = server.create('zone-role', {
    employee,
    roleName: role.legacyRole,
    user,
    zone,
    confirmedAt: confirmed ? faker.date.past() : null,
    lastSeenAt,
  });

  const roleAssignment = server.create('role-assignment', {
    editable,
    manuallyAdded,
    userId: user.id,
    roleId,
    roleScopeId: zone?.id,
    roleScopeName: zone?.name,
    roleScopeType: RoleScopeType.ZONE,

    _lastSeenAt: lastSeenAt,
  });

  return { role: legacyRole, roleAssignment };
}

export function assignCustomRoleToUser(user, role, roleScopeId) {
  server.create('role-assignment', {
    userId: user.id,
    roleId: role.id,
    roleScopeId,
    roleScopeType: role.roleScope,
  });

  if (role.legacyRole) {
    const employee = server.schema.employees.findBy({ userId: user.id }) ?? null;
    if (role.roleScope === RoleScopeType.COMPANY) {
      server.create('company-role', {
        companyId: roleScopeId,
        roleName: role.legacyRole,
        user,
        employee,
      });
    } else if (role.roleScope === RoleScopeType.LOCATION) {
      server.create('location-role', {
        locationId: roleScopeId,
        roleName: role.legacyRole,
        user,
        employee,
      });
    } else if (role.roleScope === RoleScopeType.ZONE) {
      server.create('zone-role', {
        zoneId: roleScopeId,
        roleName: role.legacyRole,
        user,
        employee,
      });
    }
  }
}

export function createDefaultCompanyRoles(server) {
  [GLOBAL_ADMIN, BILLING].forEach((roleName) => {
    server.create('authz/role', {
      id: ROLE_NAME_TO_ID[roleName],
      legacyRole: getLegacyRoleName(roleName),
      name: getRoleName(roleName),
      roleScope: RoleScopeType.COMPANY,
      permissions: COMPANY_PERMISSIONS_BY_ROLE[roleName],
      type: 'default',
    });
  });
}

export function createDefaultLocationRoles(server) {
  [DELIVERIES_ADMIN, EMPLOYEE, LOCATION_ADMIN, RECEPTIONIST, SECURITY].forEach((roleName) => {
    server.create('authz/role', {
      id: ROLE_NAME_TO_ID[roleName],
      legacyRole: getLegacyRoleName(roleName),
      name: getRoleName(roleName),
      roleScope: RoleScopeType.LOCATION,
      permissions: LOCATION_PERMISSIONS_BY_ROLE[roleName],
      type: 'default',
    });
  });
}

export function createDefaultZoneRoles(server) {
  [ZONE_ADMIN, ZONE_SECURITY].forEach((roleName) => {
    server.create('authz/role', {
      id: ROLE_NAME_TO_ID[roleName],
      legacyRole: getLegacyRoleName(roleName),
      name: getRoleName(roleName),
      roleScope: RoleScopeType.ZONE,
      permissions: ZONE_PERMISSIONS_BY_ROLE[roleName],
      type: 'default',
    });
  });
}

export function createDefaultRoles(server) {
  createDefaultCompanyRoles(server);
  createDefaultLocationRoles(server);
  createDefaultZoneRoles(server);
}

export function createCustomRole(name, roleToCopy, { roleScope, withPermissions = [], withoutPermissions = [] }) {
  let role;
  if (typeof roleToCopy === 'string') {
    // given a legacy role name or role ID
    const roleId = ROLE_NAME_TO_ID[roleToCopy] ?? roleToCopy;
    role = server.schema['authz/roles'].find(roleId);
  } else {
    // given a Role object
    role = roleToCopy;
  }

  const permissions = new Set(role.permissions);
  for (const permissionToAdd of withPermissions) {
    if (!permissionToAdd) {
      throw 'You called createCustomRole with an undefined permission in `withPermissions`. Check for a typo.';
    }
    permissions.add(permissionToAdd);
  }

  for (const permissionToRemove of withoutPermissions) {
    if (!permissionToRemove) {
      throw 'You called createCustomRole with an undefined permission in `withoutPermissions`. Check for a typo.';
    }
    permissions.delete(permissionToRemove);
  }

  return server.create('authz/role', {
    name,
    roleScope,
    permissions: Array.from(permissions),
    legacyRole: role.legacyRole,
  });
}

export function createCustomCompanyRole(name, roleToCopy, options = {}) {
  return createCustomRole(name, roleToCopy, {
    roleScope: RoleScopeType.COMPANY,
    ...options,
  });
}

export function createCustomLocationRole(name, roleToCopy, options = {}) {
  return createCustomRole(name, roleToCopy, {
    roleScope: RoleScopeType.LOCATION,
    ...options,
  });
}

export function createCustomZoneRole(name, roleToCopy, options = {}) {
  return createCustomRole(name, roleToCopy, {
    roleScope: RoleScopeType.ZONE,
    ...options,
  });
}
